import { useCallback, useEffect, useState } from 'react';
import { Navigate, Route, Routes, useNavigate, useParams } from 'react-router';
import { ChevronRight, LifeBuoy, Menu, Plus } from 'lucide-react';
import type { SupportTicketCreated, SupportTicketDetail } from '@spawnwire/shared';
import { Button, clsx, EmptyState, IconButton, Spinner } from '../../components/ui';
import { errorMessage, http } from '../../lib/api';
import { onReady } from '../../lib/gateway';
import { useIsMobile } from '../../lib/hooks';
import { formatTimestamp } from '../../lib/time';
import { ui } from '../../store/ui';
import { minecraft, useMinecraft } from '../minecraft/store';
import { support, useSupport } from './store';
import { BackLink, Conversation, PersonReply, StatusPill, TicketForm, TicketHeader, ticketTopic } from './TicketParts';

function Loading() {
  return (
    <div className="flex flex-1 items-center justify-center">
      <Spinner className="size-8" />
    </div>
  );
}

function TicketList() {
  const navigate = useNavigate();
  const tickets = useSupport((s) => s.tickets);
  const error = useSupport((s) => s.error);
  if (!tickets) {
    return error ? <EmptyState icon={LifeBuoy} title="Could not load your tickets" body={error} action={<Button onClick={() => void support.load()}>Try again</Button>} /> : <Loading />;
  }
  return (
    <div className="mx-auto w-full max-w-3xl">
      <section className="mb-6 rounded-xl border border-line bg-sidebar p-5 sm:p-6">
        <div className="flex items-start gap-4">
          <span className="hidden size-14 shrink-0 items-center justify-center rounded-xl bg-main/70 sm:flex">
            <LifeBuoy className="size-7 text-muted" aria-hidden />
          </span>
          <div className="min-w-0">
            <h2 className="text-lg font-bold text-fg sm:text-xl">How can we help?</h2>
            <p className="mt-1.5 text-sm leading-relaxed text-muted">
              Ask Spawnwire staff about your game servers, your account, or anything that isn't working. We reply here, and you get a message from Spawnwire when we do.
            </p>
            <Button icon={Plus} className="mt-4" onClick={() => navigate('/app/support/new')}>
              New ticket
            </Button>
          </div>
        </div>
      </section>
      {tickets.length === 0 ? (
        <p className="rounded-xl border border-dashed border-line p-8 text-center text-sm text-faint">You haven't opened any tickets yet.</p>
      ) : (
        <>
          <h2 className="mb-3 text-xs font-semibold uppercase tracking-wide text-faint">Your tickets — {tickets.length}</h2>
          <ul className="space-y-2">
            {tickets.map((t) => (
              <li key={t.id}>
                <button
                  type="button"
                  onClick={() => navigate(`/app/support/${t.id}`)}
                  className="flex w-full items-center gap-3 rounded-xl border border-line bg-sidebar p-4 text-left transition-colors hover:bg-elevated sm:gap-4"
                >
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center gap-2">
                      {t.unread && <span className="size-2 shrink-0 rounded-full bg-danger" role="img" aria-label="New reply" />}
                      <span className={clsx('truncate text-fg', t.unread ? 'font-bold' : 'font-semibold')}>{t.subject}</span>
                    </span>
                    <span className="block truncate text-sm text-muted">
                      #{t.number} · {ticketTopic(t)} · Updated {formatTimestamp(t.updatedAt)}
                    </span>
                  </span>
                  <StatusPill status={t.status} />
                  <ChevronRight className="hidden size-5 shrink-0 text-faint sm:block" aria-hidden />
                </button>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

function NewTicket() {
  const navigate = useNavigate();
  const overview = useMinecraft((s) => s.overview);
  useEffect(() => {
    if (!overview) void minecraft.load();
  }, [overview]);
  return (
    <div className="mx-auto w-full max-w-xl">
      <BackLink to="/app/support">Your tickets</BackLink>
      <h2 className="mt-4 text-xl font-bold text-fg">New support ticket</h2>
      <p className="mt-1 text-sm text-muted">Tell us what's going on. You'll get a message from Spawnwire when staff reply.</p>
      <div className="mt-5 rounded-xl border border-line bg-sidebar p-5">
        <TicketForm
          askEmail={false}
          defaultCategory="game_server"
          servers={overview?.servers.map((s) => ({ id: s.id, name: s.name }))}
          onSubmit={async (values) => {
            const { ticket } = await http.post<SupportTicketCreated>('/support/tickets', values);
            support.upsert(ticket);
            navigate(`/app/support/${ticket.id}`, { replace: true });
          }}
        />
      </div>
    </div>
  );
}

function TicketPage() {
  const { ticketId = '' } = useParams();
  const navigate = useNavigate();
  const [ticket, setTicket] = useState<SupportTicketDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const liveUpdatedAt = useSupport((s) => s.tickets?.find((t) => t.id === ticketId)?.updatedAt);

  const show = useCallback((t: SupportTicketDetail) => {
    setTicket(t);
    support.upsert(t);
  }, []);

  const load = useCallback(async () => {
    try {
      show(await http.get<SupportTicketDetail>(`/support/tickets/${ticketId}`));
      setError(null);
    } catch (err) {
      setError(errorMessage(err));
    }
  }, [ticketId, show]);

  useEffect(() => {
    setTicket(null);
    void load();
  }, [load]);

  // Staff replied while it is open: fetch the new message (which also marks it read).
  const shownUpdatedAt = ticket?.updatedAt;
  useEffect(() => {
    if (liveUpdatedAt && shownUpdatedAt && liveUpdatedAt !== shownUpdatedAt) void load();
  }, [liveUpdatedAt, shownUpdatedAt, load]);

  if (!ticket) {
    return error ? (
      <EmptyState icon={LifeBuoy} title="Ticket not found" body={error} action={<Button onClick={() => navigate('/app/support')}>Your tickets</Button>} />
    ) : (
      <Loading />
    );
  }
  return (
    <div className="mx-auto w-full max-w-3xl space-y-5">
      <BackLink to="/app/support">Your tickets</BackLink>
      <TicketHeader ticket={ticket} />
      <Conversation messages={ticket.messages} who={(m) => (m.fromStaff ? 'Spawnwire Support' : 'You')} />
      <PersonReply ticket={ticket} send={async (action, body) => show(await http.post<SupportTicketDetail>(`/support/tickets/${ticket.id}/${action}`, body ?? {}))} />
    </div>
  );
}

/** Your support tickets: the list, a new ticket, and each ticket's conversation. Beside Game Servers in the home sidebar. */
export function SupportView() {
  const isMobile = useIsMobile();

  useEffect(() => {
    void support.load();
    const off = onReady(() => void support.load());
    return () => {
      off();
    };
  }, []);

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <header className="flex h-12 shrink-0 items-center gap-3 border-b border-line px-3">
        {isMobile && <IconButton icon={Menu} label="Open navigation" onClick={() => ui.setMobilePane('nav')} />}
        <span className="flex items-center gap-2 font-semibold text-fg">
          <LifeBuoy className="size-5 text-muted" aria-hidden /> Support
        </span>
      </header>
      <div className="scroll-thin flex min-h-0 flex-1 flex-col overflow-y-auto p-4 sm:p-6">
        <Routes>
          <Route index element={<TicketList />} />
          <Route path="new" element={<NewTicket />} />
          <Route path=":ticketId" element={<TicketPage />} />
          <Route path="*" element={<Navigate to="/app/support" replace />} />
        </Routes>
      </div>
    </div>
  );
}
